/**
 * The colour ramps behind the heatmap maker.
 *
 * Every ramp here was chosen because its lightness changes steadily from one end to the
 * other (or, for the two diverging ramps, from the middle to both ends), which is what
 * lets a reader order the cells even in grayscale or with a colour-vision deficiency.
 *
 * Licences — the notices below are reproduced because two of the licences ask for it.
 *
 * Viridis: Nathaniel J. Smith, Stéfan van der Walt and Eric Firing. Released under CC0 1.0
 * (public-domain dedication), https://creativecommons.org/publicdomain/zero/1.0/ — credit
 * is requested, not required. The 256 samples are those of matplotlib's `viridis`.
 *
 * Cividis: Jamie R. Nuñez, Christopher R. Anderton and Ryan S. Renslow, "Optimizing
 * colormaps with consideration for color vision deficiency to enable accurate
 * interpretation of scientific data", PLOS ONE 13(7): e0199239 (2018). The 256 samples
 * are those of matplotlib's `cividis`. Licence: Copyright (c) 2017, Battelle Memorial
 * Institute. Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 * redistributions of source code must retain the above copyright notice, this list of
 * conditions and the following disclaimers; redistributions in binary form must reproduce
 * the above copyright notice, this list of conditions and the following disclaimer in the
 * documentation and/or other materials provided with the distribution; and, other than as
 * used herein, neither the name Battelle Memorial Institute nor Battelle may be used in
 * any form whatsoever without the express written consent of Battelle. THIS SOFTWARE IS
 * PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED
 * WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL BATTELLE OR
 * CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR
 * CONSEQUENTIAL DAMAGES ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE.
 *
 * Blues, RdBu, PuOr: ColorBrewer, Copyright (c) 2002 Cynthia Brewer, Mark Harrower, and
 * The Pennsylvania State University. Licensed under the Apache License, Version 2.0
 * (http://www.apache.org/licenses/LICENSE-2.0), distributed on an "AS IS" basis without
 * warranties or conditions of any kind. This product includes color specifications and
 * designs developed by Cynthia Brewer (http://colorbrewer.org/). The name "ColorBrewer"
 * is not used to endorse or promote this product.
 */

export type HeatmapPaletteId =
  | "viridis"
  | "cividis"
  | "blues"
  | "rdbu"
  | "puor";

export type PaletteKind = "sequential" | "diverging";

export interface HeatmapPalette {
  id: HeatmapPaletteId;
  kind: PaletteKind;
  /** Colours from the low end (or the "low" side of a diverging ramp) to the high end. */
  stops: readonly string[];
}

/** 256 evenly spaced samples of matplotlib's viridis, as `rrggbb` pairs run together. */
const VIRIDIS =
  "44015444025645045745055946075a46085c460a5d460b5e470d60470e61471063471164471365481467481668481769" +
  "48186a481a6c481b6d481c6e481d6f481f70482071482173482374482475482576482677482878482979472a7a472c7a" +
  "472d7b472e7c472f7d46307e46327e46337f463480453581453781453882443983443a83443b84433d84433e85423f85" +
  "4240864241864142874144874045884046883f47883f48893e49893e4a893e4c8a3d4d8a3d4e8a3c4f8a3c508b3b518b" +
  "3b528b3a538b3a548c39558c39568c38588c38598c375a8c375b8d365c8d365d8d355e8d355f8d34608d34618d33628d" +
  "33638d32648e32658e31668e31678e31688e30698e306a8e2f6b8e2f6c8e2e6d8e2e6e8e2e6f8e2d708e2d718e2c718e" +
  "2c728e2c738e2b748e2b758e2a768e2a778e2a788e29798e297a8e297b8e287c8e287d8e277e8e277f8e27808e26818e" +
  "26828e26828e25838e25848e25858e24868e24878e23888e23898e238a8d228b8d228c8d228d8d218e8d218f8d21908d" +
  "21918c20928c20928c20938c1f948c1f958b1f968b1f978b1f988b1f998a1f9a8a1e9b8a1e9c891e9d891f9e891f9f88" +
  "1fa0881fa1881fa1871fa28720a38620a48621a58521a68522a78522a88423a98324aa8325ab8225ac8226ad8127ad81" +
  "28ae8029af7f2ab07f2cb17e2db27d2eb37c2fb47c31b57b32b67a34b67935b77937b87838b9773aba763bbb753dbc74" +
  "3fbc7340bd7242be7144bf7046c06f48c16e4ac16d4cc26c4ec36b50c46a52c56954c56856c66758c7655ac8645cc863" +
  "5ec96260ca6063cb5f65cb5e67cc5c69cd5b6ccd5a6ece5870cf5773d05675d05477d1537ad1517cd2507fd34e81d34d" +
  "84d44b86d54989d5488bd6468ed64590d74393d74195d84098d83e9bd93c9dd93ba0da39a2da37a5db36a8db34aadc32" +
  "addc30b0dd2fb2dd2db5de2bb8de29bade28bddf26c0df25c2df23c5e021c8e020cae11fcde11dd0e11cd2e21bd5e21a" +
  "d8e219dae319dde318dfe318e2e418e5e419e7e419eae51aece51befe51cf1e51df4e61ef6e620f8e621fbe723fde725";

/** 256 evenly spaced samples of matplotlib's cividis. */
const CIVIDIS =
  "00224e00234f00245100255300255400265600275800285900285b00295d002a5f002a61002b62002c64002c66002d68" +
  "002e6a002e6c002f6d00306f0030700031700031710132710533710833700c34700f357012357014367016377018376f" +
  "1a386f1c396f1e3a6f203a6f213b6e233c6e243c6e263d6e273e6e293f6e2a3f6d2b406d2d416d2e416d2f426d31436d" +
  "32436d33446d34456c35456c36466c38476c39486c3a486c3b496c3c4a6c3d4a6c3e4b6c3f4c6c404c6c414d6c424e6c" +
  "434e6c444f6c45506c46516c47516c48526c49536c4a536c4b546c4c556c4d556c4e566c4f576c50576c51586d52596d" +
  "535a6d545a6d555b6d555c6d565c6d575d6d585e6d595e6e5a5f6e5b606e5c616e5d616e5e626e5e636f5f636f60646f" +
  "61656f62656f636670646770656870656870666970676a71686a71696b716a6c716b6d726c6d726c6e726d6f726e6f73" +
  "6f70737071737172747272747273747374757474757575757676767777767777777878777979777a7a787b7a787c7b78" +
  "7d7c787e7c787e7d787f7e78807f78817f788280798381798482798582798683798784788885788985788a86788b8778" +
  "8c88788d88788e89788f8a78908b78918b78928c78928d78938e78948e77958f779690779791779892779992779a9376" +
  "9b94769c95769d95769e96769f9775a09875a19975a29975a39a74a49b74a59c74a69c74a79d73a89e73a99f73aaa073" +
  "aba072aca172ada272aea371afa471b0a571b1a570b3a670b4a76fb5a86fb6a96fb7a96eb8aa6eb9ab6dbaac6dbbad6d" +
  "bcae6cbdae6cbeaf6bbfb06bc0b16ac1b26ac2b369c3b369c4b468c5b568c6b667c7b767c8b866c9b965cbb965ccba64" +
  "cdbb63cebc63cfbd62d0be62d1bf61d2c060d3c05fd4c15fd5c25ed6c35dd7c45cd9c55cdac65bdbc75adcc859ddc858" +
  "dec958dfca57e0cb56e1cc55e2cd54e4ce53e5cf52e6d051e7d150e8d24fe9d34eead34cebd44bedd54aeed649efd748" +
  "f0d846f1d945f2da44f3db42f5dc41f6dd3ff7de3ef8df3cf9e03afbe138fce236fde334fee434fee535fee636fee838";

function unpack(packed: string): string[] {
  const colors: string[] = [];
  for (let index = 0; index < packed.length; index += 6) {
    colors.push(`#${packed.slice(index, index + 6)}`);
  }
  return colors;
}

export const HEATMAP_PALETTES: readonly HeatmapPalette[] = [
  { id: "viridis", kind: "sequential", stops: unpack(VIRIDIS) },
  { id: "cividis", kind: "sequential", stops: unpack(CIVIDIS) },
  {
    id: "blues",
    kind: "sequential",
    stops: [
      "#f7fbff",
      "#deebf7",
      "#c6dbef",
      "#9ecae1",
      "#6baed6",
      "#4292c6",
      "#2171b5",
      "#08519c",
      "#08306b",
    ],
  },
  {
    id: "rdbu",
    kind: "diverging",
    stops: [
      "#67001f",
      "#b2182b",
      "#d6604d",
      "#f4a582",
      "#fddbc7",
      "#f7f7f7",
      "#d1e5f0",
      "#92c5de",
      "#4393c3",
      "#2166ac",
      "#053061",
    ],
  },
  {
    id: "puor",
    kind: "diverging",
    stops: [
      "#7f3b08",
      "#b35806",
      "#e08214",
      "#fdb863",
      "#fee0b6",
      "#f7f7f7",
      "#d8daeb",
      "#b2abd2",
      "#8073ac",
      "#542788",
      "#2d004b",
    ],
  },
];
